import React from 'react';
import {PickupTaskTypeWithFilter, PickupTaskStatus} from '../types/PickupTaskType';
import {usePickupData} from '../contexts/PickupDataContext';
import { useQuery } from '@apollo/client';
import { GET_PICKUP_ROUTES_BY_WAREHOUSE } from '../graphql/queries/getPickupRoutes';
import { PickupRouteType } from '../graphql/types/PickupRouteTypes';
import { useTranslation } from 'react-i18next';
import { useWarehousePickupZones } from '../../../../hooks/useWarehousePickupZones';
import { usePickupTaskInternationalization } from '../hooks/usePickupTaskInternationalization';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import ParcelFilterStyle from '../../../delivery/components/ParcelFilterStyle';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import DateRangeFilterItem from "./DateRangeFilterItem";

export interface PickupTaskFilterProps {
    filter: Partial<PickupTaskTypeWithFilter>;
    onFilterChange: (filter: Partial<PickupTaskTypeWithFilter>) => void;
}

const PickupTaskFilter: React.FC<PickupTaskFilterProps> = ({
                                                               filter,
                                                               onFilterChange,
                                                           }) => {
    const {drivers, vehicles, driversLoading, vehiclesLoading, warehouseId} = usePickupData();
    const { pickupZones, loading: pickupZonesLoading } = useWarehousePickupZones(warehouseId ? Number(warehouseId) : 0);

    // 获取 pickup routes 列表
    const { data: routesData, loading: routesLoading } = useQuery(GET_PICKUP_ROUTES_BY_WAREHOUSE, {
        variables: { warehouseId: warehouseId ? parseInt(warehouseId) : 0 },
        skip: !warehouseId,
    });

    const {t} = useTranslation();
    
    // 🔧 使用国际化Hook
    const { taskStatusOptions } = usePickupTaskInternationalization();

    const labelStatus = String(t('pickup.pickupTask.filters.status'));
    const labelTaskNumber = String(t('pickup.pickupTask.filters.taskNumber'));
    const labelDriver = String(t('pickup.pickupTask.filters.driver'));
    const labelRoute = String(t('pickup.pickupTask.filters.route'));
    const labelVehicle = String(t('pickup.pickupTask.filters.vehicle'));
    const labelTaskDate = String(t('pickup.pickupTask.filters.taskDate'));
    const labelPickupSite = String(t('pickup.pickupTask.filters.pickupSite') || '揽收站点ID');
    const labelAddress = String(t('pickup.pickupTask.filters.address') || '地址');
    const labelZipcode = String(t('pickup.pickupTask.filters.zipcode') || '邮编');

    const col1Labels = [labelStatus, labelRoute, labelPickupSite];
    const col2Labels = [labelTaskNumber, labelVehicle, labelAddress];
    const col3Labels = [labelDriver, labelTaskDate, labelZipcode];
    
    const routeOptions = routesData?.getPickupRoutesByWarehouseId?.map((route: PickupRouteType) => ({
        label: route.routeName,
        value: route.id.toString()
    })) || [];
    
    const driverOptions = drivers.map((driver) => ({
        label: driver.driverName,
        value: driver.accountId.toString()
    }));
    
    const vehicleOptions = vehicles.map((vehicle) => ({
        label: vehicle.label,
        value: vehicle.value.toString()
    }));

    return (
        <div className="mb-2 pl-0 py-2">
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelStatus}
                        options={taskStatusOptions}
                        value={filter.status}
                        onChange={(value: string) => onFilterChange({...filter, status: (value as PickupTaskStatus) || undefined})}
                        spanClassName={getSmartLabelClassName(col1Labels)}
                        className="w-full"
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelTaskNumber}
                        value={filter.taskNumber}
                        onChange={e => onFilterChange({...filter, taskNumber: e.target.value})}
                        spanClassName={getSmartLabelClassName(col2Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelDriver}
                        options={driverOptions}
                        value={filter.accountId}
                        onChange={(value: string) => onFilterChange({...filter, accountId: value || undefined})}
                        spanClassName={getSmartLabelClassName(col3Labels)}
                        className="w-full"
                        showSearch
                        placeholder={t('pickup.pickupTask.placeholders.selectDriver') || undefined}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelRoute}
                        options={routeOptions}
                        value={filter.routeId?.toString()}
                        onChange={(value: string) => onFilterChange({...filter, routeId: value ? parseInt(value) : undefined})}
                        spanClassName={getSmartLabelClassName(col1Labels)}
                        className="w-full"
                        showSearch
                        placeholder={t('pickup.pickupTask.placeholders.selectRoute') || undefined}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelVehicle}
                        options={vehicleOptions}
                        value={filter.vehicleId}
                        onChange={(value: string) => onFilterChange({...filter, vehicleId: value || undefined})}
                        spanClassName={getSmartLabelClassName(col2Labels)}
                        className="w-full"
                        showSearch
                        placeholder={t('pickup.pickupTask.placeholders.selectVehicle') || undefined}
                    />
                </div>
                <div className="min-w-0">
                    <DateRangeFilterItem
                        label={labelTaskDate}
                        value={filter.taskDateStart && filter.taskDateEnd ? [filter.taskDateStart, filter.taskDateEnd] : null}
                        onChange={(dates) => {
                            onFilterChange({
                                ...filter,
                                taskDateStart: dates?.[0] || undefined,
                                taskDateEnd: dates?.[1] || undefined
                            });
                        }}
                        spanClassName={getSmartLabelClassName(col3Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelPickupSite}
                        value={filter.cooperationWarehouseId}
                        onChange={e => onFilterChange({...filter, cooperationWarehouseId: e.target.value.trim() || undefined})}
                        spanClassName={getSmartLabelClassName(col1Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <InputFilterItem
                        label={labelAddress}
                        value={filter.address}
                        onChange={e => onFilterChange({...filter, address: e.target.value.trim() || undefined})}
                        spanClassName={getSmartLabelClassName(col2Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelZipcode}
                        options={pickupZones}
                        value={filter.zipcodeList ?? undefined}
                        onChange={(value: string | string[]) => onFilterChange({
                            ...filter,
                            zipcodeList: Array.isArray(value) && value.length > 0 ? value : undefined
                        })}
                        spanClassName={getSmartLabelClassName(col3Labels)}
                        className="w-full"
                        mode="multiple"
                        showSearch
                        loading={pickupZonesLoading}
                        maxTagCount="responsive"
                        placeholder={t('pickup.appointment.placeholders.selectZipcode') || undefined}
                        disabled={!warehouseId || pickupZones.length === 0}
                    />
                </div>
            </div>
        </div>
    );
};

export default PickupTaskFilter; 